import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Plus, Edit, Phone, Mail, User, Thermometer, ChevronDown, ChevronUp, MessageCircle, Send, CalendarClock, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const NEIGHBORHOODS = [
  "Barra da Tijuca", "Recreio dos Bandeirantes", "Copacabana", "Ipanema",
  "Leblon", "Botafogo", "Flamengo", "Laranjeiras", "Tijuca", "Alto da Boa Vista",
];

const TEMPERATURE_OPTIONS = [
  { value: "frio", label: "Frio", color: "bg-blue-100 text-blue-800 border-blue-300" },
  { value: "morno", label: "Morno", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  { value: "quente", label: "Quente", color: "bg-red-100 text-red-800 border-red-300" },
];

const STATUS_OPTIONS = [
  { value: "novo", label: "Novo" },
  { value: "em_atendimento", label: "Em Atendimento" },
  { value: "visita_agendada", label: "Visita Agendada" },
  { value: "visita_realizada", label: "Visita Realizada" },
  { value: "documentacao", label: "Documentação" },
  { value: "sinal", label: "Sinal" },
  { value: "escritura", label: "Escritura" },
  { value: "rgi", label: "RGI" },
  { value: "concluido", label: "Concluído" },
  { value: "perdido", label: "Perdido" },
];

interface CrmLead {
  id: string;
  client_name: string;
  client_phone: string | null;
  client_email: string | null;
  property_name: string | null;
  neighborhood: string | null;
  temperature: "frio" | "morno" | "quente";
  status: string;
  visit_date: string | null;
  visit_result: string | null;
  documentation_notes: string | null;
  deposit_notes: string | null;
  deed_notes: string | null;
  rgi_notes: string | null;
  general_notes: string | null;
  follow_up_at: string | null;
  follow_up_notes: string | null;
  follow_up_done: boolean | null;
  created_at: string;
  updated_at: string;
}

const emptyForm = {
  client_name: "",
  client_phone: "",
  client_email: "",
  property_name: "",
  neighborhood: "",
  temperature: "frio" as "frio" | "morno" | "quente",
  status: "novo",
  visit_date: "",
  visit_result: "",
  documentation_notes: "",
  deposit_notes: "",
  deed_notes: "",
  rgi_notes: "",
  general_notes: "",
  follow_up_at: "",
  follow_up_notes: "",
};

type SegmentKey = "imoveis" | "agenda" | "avaliacao" | "anuncie";

interface ExtraField {
  key: string;
  label: string;
  options?: string[];
}

const SEGMENTS: Record<
  SegmentKey,
  { label: string; propertyLabel: string; match: (l: CrmLead) => boolean; extras: ExtraField[] }
> = {
  imoveis: {
    label: "Imóveis",
    propertyLabel: "",
    match: (l) => {
      const p = (l.property_name || "").toLowerCase();
      return (
        !p.includes("avaliação de imóvel") &&
        !p.includes("anuncie seu imóvel") &&
        !p.startsWith("agenda:")
      );
    },
    extras: [],
  },
  agenda: {
    label: "Agenda",
    propertyLabel: "Agenda: visita",
    match: (l) => (l.property_name || "").toLowerCase().startsWith("agenda:"),
    extras: [
      { key: "Dia preferido para a visita", label: "Dia preferido para a visita" },
      { key: "Horário preferido", label: "Horário preferido" },
    ],
  },
  avaliacao: {
    label: "Avaliação",
    propertyLabel: "Avaliação de imóvel (proprietário)",
    match: (l) => (l.property_name || "").toLowerCase().includes("avaliação de imóvel"),
    extras: [
      { key: "Endereço do imóvel", label: "Endereço do imóvel" },
      { key: "Tipo de imóvel", label: "Tipo de imóvel", options: ["Apartamento", "Cobertura", "Casa", "Studio", "Sala comercial"] },
      { key: "Área (m²)", label: "Área (m²)" },
      { key: "Quartos", label: "Quartos", options: ["1", "2", "3", "4 ou mais"] },
      { key: "Finalidade da avaliação", label: "Finalidade da avaliação", options: ["Venda", "Locação", "Inventário / partilha", "Financiamento", "Somente referência de preço"] },
      { key: "Prazo para vender", label: "Prazo para vender", options: ["Imediato", "Até 30 dias", "Até 90 dias", "Sem pressa"] },
    ],
  },
  anuncie: {
    label: "Anuncie seu Imóvel",
    propertyLabel: "Anuncie seu imóvel (proprietário)",
    match: (l) => (l.property_name || "").toLowerCase().includes("anuncie seu imóvel"),
    extras: [
      { key: "Endereço do imóvel", label: "Endereço do imóvel" },
      { key: "Tipo de imóvel", label: "Tipo de imóvel", options: ["Apartamento", "Cobertura", "Casa", "Studio", "Sala comercial"] },
      { key: "Área (m²)", label: "Área (m²)" },
      { key: "Quartos", label: "Quartos", options: ["1", "2", "3", "4 ou mais"] },
      { key: "Vagas", label: "Vagas", options: ["0", "1", "2", "3 ou mais"] },
      { key: "Valor pretendido", label: "Valor pretendido (R$)" },
      { key: "Situação do imóvel", label: "Situação do imóvel", options: ["Vago", "Ocupado pelo proprietário", "Alugado"] },
      { key: "Exclusividade", label: "Aceita exclusividade?", options: ["Sim", "Não", "Quer entender melhor"] },
    ],
  },
};

/** Inline follow-up scheduler for a single lead. */
const FollowUpEditor = ({
  lead,
  onSave,
  onToggleDone,
}: {
  lead: CrmLead;
  onSave: (at: string, notes: string) => void;
  onToggleDone: () => void;
}) => {
  const [at, setAt] = useState(lead.follow_up_at ? lead.follow_up_at.slice(0, 16) : "");
  const [notes, setNotes] = useState(lead.follow_up_notes || "");

  return (
    <div className="rounded-md border border-border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground font-body">
          Follow up
        </label>
        {lead.follow_up_at && (
          <Button type="button" variant="outline" size="sm" onClick={onToggleDone}>
            <Check className="h-4 w-4" />
            {lead.follow_up_done ? "Reabrir" : "Concluir"}
          </Button>
        )}
      </div>
      <Input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} className="font-body text-sm" />
      <Textarea
        placeholder="Próximo passo / o que foi combinado"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        maxLength={1000}
        className="font-body text-sm"
      />
      <Button type="button" variant="gold" size="sm" onClick={() => onSave(at, notes)}>
        <CalendarClock className="h-4 w-4" /> Salvar follow up
      </Button>
    </div>
  );
};

const CrmAdmin = () => {
  const { toast } = useToast();
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [segment, setSegment] = useState<SegmentKey>("imoveis");
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [expandedLead, setExpandedLead] = useState<string | null>(null);
  const [filterTemp, setFilterTemp] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");


  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    const { data } = await supabase
      .from("crm_leads")
      .select("*")
      .order("updated_at", { ascending: false });
    if (data) setLeads(data as unknown as CrmLead[]);
  };

  const resetForm = () => {
    setForm({ ...emptyForm });
    setExtras({});
    setEditingId(null);
  };

  const changeSegment = (key: SegmentKey) => {
    setSegment(key);
    resetForm();
  };


  const handleEdit = (lead: CrmLead) => {
    setEditingId(lead.id);
    setForm({
      client_name: lead.client_name,
      client_phone: lead.client_phone || "",
      client_email: lead.client_email || "",
      property_name: lead.property_name || "",
      neighborhood: lead.neighborhood || "",
      temperature: lead.temperature,
      status: lead.status,
      visit_date: lead.visit_date ? lead.visit_date.slice(0, 16) : "",
      visit_result: lead.visit_result || "",
      documentation_notes: lead.documentation_notes || "",
      deposit_notes: lead.deposit_notes || "",
      deed_notes: lead.deed_notes || "",
      rgi_notes: lead.rgi_notes || "",
      general_notes: lead.general_notes || "",
      follow_up_at: lead.follow_up_at ? lead.follow_up_at.slice(0, 16) : "",
      follow_up_notes: lead.follow_up_notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cfg = SEGMENTS[segment];
    const extrasSummary = cfg.extras
      .filter((f) => extras[f.key]?.trim())
      .map((f) => `${f.label}: ${extras[f.key].trim()}`)
      .join(" | ");
    const notes = [extrasSummary, form.general_notes].filter(Boolean).join(" | ");

    const payload = {
      client_name: form.client_name,
      client_phone: form.client_phone || null,
      client_email: form.client_email || null,
      property_name: form.property_name || cfg.propertyLabel || null,
      neighborhood: form.neighborhood || null,
      temperature: form.temperature,
      status: form.status,
      visit_date: form.visit_date ? new Date(form.visit_date).toISOString() : null,
      visit_result: form.visit_result || null,
      documentation_notes: form.documentation_notes || null,
      deposit_notes: form.deposit_notes || null,
      deed_notes: form.deed_notes || null,
      rgi_notes: form.rgi_notes || null,
      general_notes: notes || null,
      follow_up_at: form.follow_up_at ? new Date(form.follow_up_at).toISOString() : null,
      follow_up_notes: form.follow_up_notes || null,
    };


    let error;
    if (editingId) {
      ({ error } = await supabase.from("crm_leads").update(payload as any).eq("id", editingId));
    } else {
      ({ error } = await supabase.from("crm_leads").insert(payload as any));
    }

    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editingId ? "Lead atualizado!" : "Lead cadastrado!" });
      resetForm();
      loadLeads();
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este lead?")) return;
    const { error } = await supabase.from("crm_leads").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Lead excluído" });
      loadLeads();
    }
  };

  const quickUpdateField = async (id: string, field: string, value: string) => {
    const { error } = await supabase.from("crm_leads").update({ [field]: value } as any).eq("id", id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      loadLeads();
    }
  };

  const getTempStyle = (temp: string) => {
    return TEMPERATURE_OPTIONS.find(t => t.value === temp)?.color || "";
  };

  const getStatusLabel = (status: string) => {
    return STATUS_OPTIONS.find(s => s.value === status)?.label || status;
  };

  const filteredLeads = leads.filter(l => {
    if (!SEGMENTS[segment].match(l)) return false;
    if (filterTemp !== "all" && l.temperature !== filterTemp) return false;
    if (filterStatus !== "all" && l.status !== filterStatus) return false;
    return true;
  });

  const cfg = SEGMENTS[segment];

  /** Total and "novo" (unattended) lead counts per CRM segment. */
  const counts = (Object.keys(SEGMENTS) as SegmentKey[]).reduce((acc, key) => {
    const list = leads.filter(SEGMENTS[key].match);
    acc[key] = { total: list.length, novos: list.filter((l) => l.status === "novo").length };
    return acc;
  }, {} as Record<SegmentKey, { total: number; novos: number }>);

  const onlyDigits = (v: string) => v.replace(/\D/g, "");

  /** WhatsApp deep link with a ready-to-send message for this lead. */
  const whatsappLink = (lead: CrmLead) => {
    let digits = onlyDigits(lead.client_phone || "");
    if (!digits) return null;
    if (digits.length <= 11) digits = `55${digits}`;
    const msg =
      `Olá ${lead.client_name.split(" ")[0]}, aqui é da TopClass Imóveis Premium. ` +
      `Recebemos seu contato${lead.property_name ? ` sobre ${lead.property_name}` : ""}` +
      `${lead.neighborhood ? ` em ${lead.neighborhood}` : ""}. Podemos falar agora?`;
    return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
  };

  /** Mailto link with subject and body pre-filled for this lead. */
  const emailLink = (lead: CrmLead) => {
    if (!lead.client_email) return null;
    const subject = `TopClass Imóveis Premium — ${lead.property_name || "seu contato"}`;
    const body =
      `Olá ${lead.client_name.split(" ")[0]},\n\n` +
      `Recebemos seu contato pelo site da TopClass Imóveis Premium` +
      `${lead.property_name ? ` sobre ${lead.property_name}` : ""}.\n\n` +
      `Podemos agendar uma conversa para entender melhor o que você procura?\n\n` +
      `Atenciosamente,\nTopClass Imóveis Premium\nWhatsApp: (21) 97354-8387`;
    return `mailto:${lead.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  /** Saves the next follow-up date / notes for a lead. */
  const saveFollowUp = async (id: string, at: string, notes: string) => {
    const { error } = await supabase
      .from("crm_leads")
      .update({
        follow_up_at: at ? new Date(at).toISOString() : null,
        follow_up_notes: notes || null,
        follow_up_done: false,
      } as any)
      .eq("id", id);
    if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Follow up salvo" });
      loadLeads();
    }
  };

  const toggleFollowUpDone = async (lead: CrmLead) => {
    const { error } = await supabase
      .from("crm_leads")
      .update({ follow_up_done: !lead.follow_up_done } as any)
      .eq("id", lead.id);
    if (error) toast({ title: "Erro", description: error.message, variant: "destructive" });
    else loadLeads();
  };

  return (
    <div className="space-y-6">
      {/* CRM segment tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        {(Object.keys(SEGMENTS) as SegmentKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => changeSegment(key)}
            className={`px-4 py-2 rounded-md text-sm font-body border transition-colors flex items-center gap-2 ${
              segment === key
                ? "bg-accent text-accent-foreground border-accent font-semibold"
                : "bg-card text-muted-foreground border-border hover:border-accent/50"
            }`}
          >
            CRM {SEGMENTS[key].label}
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-muted text-foreground border border-border">
              {counts[key].total}
            </span>
            {counts[key].novos > 0 && (
              <span
                title={`${counts[key].novos} novo(s) contato(s)`}
                className="text-xs px-1.5 py-0.5 rounded-full bg-red-600 text-white font-semibold"
              >
                {counts[key].novos} novo{counts[key].novos > 1 ? "s" : ""}
              </span>
            )}
          </button>
        ))}
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Form */}
      <div className="lg:col-span-1">
        <form onSubmit={handleSubmit} className="bg-card rounded-lg p-6 shadow-lg border border-border space-y-3">
          <h2 className="font-display text-lg font-bold text-foreground mb-2">
            {editingId ? "Editar Lead" : `Novo Lead — ${cfg.label}`}
          </h2>

          <Input placeholder="Nome do Cliente *" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required maxLength={200} className="font-body" />
          <Input placeholder="Telefone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} maxLength={20} className="font-body" />
          <Input placeholder="E-mail" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} maxLength={200} className="font-body" />
          <Input placeholder={segment === "imoveis" ? "Nome do Imóvel" : `Origem (padrão: ${cfg.propertyLabel})`} value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} maxLength={200} className="font-body" />

          {cfg.extras.length > 0 && (
            <div className="space-y-3 rounded-md border border-border p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground font-body">
                Dados do {cfg.label}
              </p>
              {cfg.extras.map((f) =>
                f.options ? (
                  <Select
                    key={f.key}
                    value={extras[f.key] || ""}
                    onValueChange={(v) => setExtras({ ...extras, [f.key]: v })}
                  >
                    <SelectTrigger className="font-body"><SelectValue placeholder={f.label} /></SelectTrigger>
                    <SelectContent>
                      {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    key={f.key}
                    placeholder={f.label}
                    value={extras[f.key] || ""}
                    onChange={(e) => setExtras({ ...extras, [f.key]: e.target.value })}
                    maxLength={300}
                    className="font-body"
                  />
                )
              )}
            </div>
          )}


          <Select value={form.neighborhood} onValueChange={(v) => setForm({ ...form, neighborhood: v })}>
            <SelectTrigger className="font-body"><SelectValue placeholder="Bairro" /></SelectTrigger>
            <SelectContent>
              {NEIGHBORHOODS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>

          <div className="grid grid-cols-2 gap-2">
            <Select value={form.temperature} onValueChange={(v) => setForm({ ...form, temperature: v as any })}>
              <SelectTrigger className="font-body"><SelectValue placeholder="Temperatura" /></SelectTrigger>
              <SelectContent>
                {TEMPERATURE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger className="font-body"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground font-body">Agendamento da Visita</label>
            <Input type="datetime-local" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="font-body" />
          </div>

          <Textarea placeholder="Resultado da Visita" value={form.visit_result} onChange={(e) => setForm({ ...form, visit_result: e.target.value })} rows={2} maxLength={1000} className="font-body" />
          <Textarea placeholder="Documentação" value={form.documentation_notes} onChange={(e) => setForm({ ...form, documentation_notes: e.target.value })} rows={2} maxLength={1000} className="font-body" />
          <Textarea placeholder="Sinal" value={form.deposit_notes} onChange={(e) => setForm({ ...form, deposit_notes: e.target.value })} rows={2} maxLength={1000} className="font-body" />
          <Textarea placeholder="Escritura" value={form.deed_notes} onChange={(e) => setForm({ ...form, deed_notes: e.target.value })} rows={2} maxLength={1000} className="font-body" />
          <Textarea placeholder="RGI" value={form.rgi_notes} onChange={(e) => setForm({ ...form, rgi_notes: e.target.value })} rows={2} maxLength={1000} className="font-body" />
          <Textarea placeholder="Observações Gerais" value={form.general_notes} onChange={(e) => setForm({ ...form, general_notes: e.target.value })} rows={2} maxLength={2000} className="font-body" />

          <div className="space-y-1.5 rounded-md border border-border p-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground font-body">Follow up</label>
            <Input type="datetime-local" value={form.follow_up_at} onChange={(e) => setForm({ ...form, follow_up_at: e.target.value })} className="font-body" />
            <Textarea placeholder="O que combinamos / próximo passo" value={form.follow_up_notes} onChange={(e) => setForm({ ...form, follow_up_notes: e.target.value })} rows={2} maxLength={1000} className="font-body" />
          </div>


          <div className="flex gap-2">
            <Button type="submit" variant="gold" className="flex-1">
              <Plus className="h-4 w-4" /> {editingId ? "Salvar" : "Cadastrar"}
            </Button>
            {editingId && <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>}
          </div>
        </form>
      </div>

      {/* List */}
      <div className="lg:col-span-2">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-lg font-bold text-foreground">
            Leads ({filteredLeads.length})
          </h2>
          <div className="flex gap-2">
            <Select value={filterTemp} onValueChange={setFilterTemp}>
              <SelectTrigger className="font-body w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Temperatura</SelectItem>
                {TEMPERATURE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="font-body w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Status</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-3">
          {filteredLeads.map((lead) => (
            <div key={lead.id} className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-body font-semibold text-foreground">{lead.client_name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getTempStyle(lead.temperature)}`}>
                      {TEMPERATURE_OPTIONS.find(t => t.value === lead.temperature)?.label}
                    </span>
                    <Badge variant="outline" className="text-xs">{getStatusLabel(lead.status)}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground font-body mt-0.5">
                    {lead.property_name && `${lead.property_name} · `}
                    {lead.neighborhood && `${lead.neighborhood} · `}
                    {new Date(lead.created_at).toLocaleDateString("pt-BR")}
                  </p>
                  {lead.client_phone && (
                    <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-0.5">
                      <Phone className="h-3 w-3" /> {lead.client_phone}
                      {lead.client_email && <> · <Mail className="h-3 w-3 ml-1" /> {lead.client_email}</>}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {whatsappLink(lead) && (
                      <Button variant="whatsapp" size="sm" asChild>
                        <a href={whatsappLink(lead)!} target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                      </Button>
                    )}
                    {emailLink(lead) && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={emailLink(lead)!}>
                          <Send className="h-4 w-4" /> E-mail
                        </a>
                      </Button>
                    )}
                    {lead.follow_up_at && (
                      <span
                        className={`text-xs px-2 py-1 rounded-full border font-body flex items-center gap-1 ${
                          lead.follow_up_done
                            ? "bg-green-100 text-green-800 border-green-300"
                            : new Date(lead.follow_up_at) <= new Date()
                            ? "bg-red-100 text-red-800 border-red-300"
                            : "bg-muted text-foreground border-border"
                        }`}
                      >
                        <CalendarClock className="h-3 w-3" />
                        Follow up {new Date(lead.follow_up_at).toLocaleString("pt-BR")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0 ml-2">
                  <Button variant="ghost" size="icon" onClick={() => setExpandedLead(expandedLead === lead.id ? null : lead.id)}>
                    {expandedLead === lead.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(lead)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(lead.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>

              </div>

              {expandedLead === lead.id && (
                <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                  {/* Quick update temperature and status */}
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={lead.temperature} onValueChange={(v) => quickUpdateField(lead.id, "temperature", v)}>
                      <SelectTrigger className="font-body text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TEMPERATURE_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={lead.status} onValueChange={(v) => quickUpdateField(lead.id, "status", v)}>
                      <SelectTrigger className="font-body text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Follow up */}
                  <FollowUpEditor
                    lead={lead}
                    onSave={(at, notes) => saveFollowUp(lead.id, at, notes)}
                    onToggleDone={() => toggleFollowUpDone(lead)}
                  />


                  {/* Progress pipeline */}
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-muted-foreground font-body uppercase tracking-wide">Progresso</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
                      {STATUS_OPTIONS.map((s) => {
                        const isActive = STATUS_OPTIONS.findIndex(x => x.value === lead.status) >= STATUS_OPTIONS.findIndex(x => x.value === s.value);
                        return (
                          <button
                            key={s.value}
                            type="button"
                            onClick={() => quickUpdateField(lead.id, "status", s.value)}
                            className={`text-xs px-2 py-1.5 rounded border transition-colors font-body ${
                              lead.status === s.value
                                ? "bg-accent text-accent-foreground border-accent font-semibold"
                                : isActive
                                ? "bg-accent/30 text-foreground border-accent/50"
                                : "bg-card text-muted-foreground border-border hover:border-accent/50"
                            }`}
                          >
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Detail notes */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm font-body">
                    {lead.visit_date && (
                      <div className="bg-muted/30 rounded p-2">
                        <span className="text-xs font-medium text-muted-foreground">Visita:</span>
                        <p className="text-foreground">{new Date(lead.visit_date).toLocaleString("pt-BR")}</p>
                      </div>
                    )}
                    {lead.visit_result && (
                      <div className="bg-muted/30 rounded p-2">
                        <span className="text-xs font-medium text-muted-foreground">Resultado da Visita:</span>
                        <p className="text-foreground">{lead.visit_result}</p>
                      </div>
                    )}
                    {lead.documentation_notes && (
                      <div className="bg-muted/30 rounded p-2">
                        <span className="text-xs font-medium text-muted-foreground">Documentação:</span>
                        <p className="text-foreground">{lead.documentation_notes}</p>
                      </div>
                    )}
                    {lead.deposit_notes && (
                      <div className="bg-muted/30 rounded p-2">
                        <span className="text-xs font-medium text-muted-foreground">Sinal:</span>
                        <p className="text-foreground">{lead.deposit_notes}</p>
                      </div>
                    )}
                    {lead.deed_notes && (
                      <div className="bg-muted/30 rounded p-2">
                        <span className="text-xs font-medium text-muted-foreground">Escritura:</span>
                        <p className="text-foreground">{lead.deed_notes}</p>
                      </div>
                    )}
                    {lead.rgi_notes && (
                      <div className="bg-muted/30 rounded p-2">
                        <span className="text-xs font-medium text-muted-foreground">RGI:</span>
                        <p className="text-foreground">{lead.rgi_notes}</p>
                      </div>
                    )}
                    {lead.general_notes && (
                      <div className="bg-muted/30 rounded p-2 sm:col-span-2">
                        <span className="text-xs font-medium text-muted-foreground">Observações:</span>
                        <p className="text-foreground">{lead.general_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          {filteredLeads.length === 0 && (
            <p className="text-muted-foreground font-body text-center py-8">
              Nenhum lead encontrado. Use o formulário para adicionar.
            </p>
          )}
        </div>
      </div>
      </div>
    </div>
  );

};

export default CrmAdmin;
